/* eslint-disable react/prop-types */
import React, { useState, forwardRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useSnackbar, SnackbarContent } from 'notistack';
import Collapse from '@mui/material/Collapse';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import clsx from 'clsx';
import { Box } from '@mui/material';
import useStyles from './styles';
import { AppState } from '../../store';

const CustomSnackbarDownloadable = forwardRef<
  HTMLDivElement,
  {
    id: string | number;
    message: string | React.ReactNode;
  }
>((properties, reference) => {
  const classes = useStyles();
  const { closeSnackbar } = useSnackbar();
  const [expanded, setExpanded] = useState(false);
  const { id, message } = properties;
  const notifications = useSelector((state: AppState) => state.snackbar.notifications);
  const currentNotification = notifications.find((notification) => notification.key === id);
  const {
    options: { variant, downloadAction, downloadMessage },
  } = currentNotification;

  const handleExpandClick = useCallback(() => {
    setExpanded((oldExpanded) => !oldExpanded);
  }, []);

  const handleDismiss = useCallback(() => {
    closeSnackbar(id);
  }, [id, closeSnackbar]);

  return (
    <SnackbarContent ref={reference} className={classes.root}>
      <Card
        className={clsx(
          classes.card,
          { [classes.success]: variant === 'success' },
          { [classes.error]: variant === 'error' },
          { [classes.warning]: variant === 'warning' },
          { [classes.info]: variant === 'info' },
        )}
      >
        <CardActions classes={{ root: classes.actionRoot }}>
          <Typography variant="subtitle2" className={clsx(classes.typography, classes.lightFont)}>
            {message}
          </Typography>
          <div className={classes.icons}>
            {downloadAction && (
              <IconButton
                aria-label="Show more"
                className={clsx(classes.expand, classes.buttonAvailable, {
                  [classes.expandOpen]: expanded,
                })}
                onClick={handleExpandClick}
                size="large">
                <ExpandMoreIcon style={{ fill: 'white' }} />
              </IconButton>
            )}
            <IconButton
              className={clsx(classes.expand, classes.lightFont, classes.buttonAvailable)}
              onClick={handleDismiss}
              size="large">
              <CloseIcon style={{ fill: 'white' }} />
            </IconButton>
          </div>
        </CardActions>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          {downloadAction && (
            <Paper className={classes.collapse}>
              <Box display="flex" justifyContent="space-between">
                <Typography gutterBottom>{downloadMessage}</Typography>
                <Button
                  onClick={() => downloadAction()}
                  size="small"
                  className={clsx(classes.button, classes.buttonAvailable)}
                >
                  <CheckCircleIcon className={classes.checkIcon} />
                  Download
                </Button>
              </Box>
            </Paper>
          )}
        </Collapse>
      </Card>
    </SnackbarContent>
  );
});

export default CustomSnackbarDownloadable;
